/**
 * Normalized Meeting State Selectors
 * 
 * This provides a platform-agnostic way to access meeting data,
 * abstracting away the differences between Zoom and Web meeting structures.
 */

// Platform detection helper
const detectPlatform = (meetingObject) => {
  if (!meetingObject) return null;
  
  // Explicit platform field
  if (meetingObject.platform) {
    return meetingObject.platform;
  }
  
  // Web meetings have meetingHost field or agenda at top level without platformMeetingId
  if (meetingObject.meetingHost || (meetingObject.agenda && !meetingObject.platformMeetingId)) {
    return 'WEB';
  }
  
  // Zoom meetings have platformMeetingId or complex meetingInstances structure
  if (meetingObject.platformMeetingId || 
      (meetingObject.meetingInstances && meetingObject.meetingInstances.length > 0 && 
       meetingObject.meetingInstances[0].platformMeetingInstanceId)) {
    return 'ZOOM';
  }
  
  return 'ZOOM'; // Default fallback for backward compatibility
};

// Core normalized meeting state selector
export const selectNormalizedMeetingState = (state) => {
  const meetingData = state.meeting.data;
  const meetingObject = meetingData.meetingObject;
  
  if (!meetingObject) {
    return {
      meetingInstanceId: null,
      hostId: null,
      agenda: null,
      participants: [],
      platform: null,
      isActive: false,
      startTime: null,
      endTime: null
    };
  }
  
  const platform = detectPlatform(meetingObject);
  
  if (platform === 'WEB') {
    // Web meeting structure: standardized meeting object with meetingInstances array
    const meetingInstance = meetingObject.meetingInstances?.[0];
    
    return {
      meetingInstanceId: meetingObject._id,
      hostId: meetingObject.hostId,
      agenda: meetingInstance?.agenda || meetingObject.agenda || null,
      participants: meetingInstance?.participantsInMeetingOnTH || [],
      platform: 'WEB',
      isActive: meetingData.inAMeeting,
      startTime: meetingObject.startTime || null,
      endTime: meetingObject.endTime || null,
      // Keep platform-specific data for gradual migration
      platformData: {
        webMeetingInstance: meetingObject,
        meetingHost: meetingObject.meetingHost
      }
    };
  }
  
  // Zoom meeting structure: meeting -> meetingInstance -> agenda
  const meetingInstance = meetingObject.meetingInstances?.[0];
  
  return {
    meetingInstanceId: meetingInstance?._id || null,
    hostId: meetingObject.hostId,
    agenda: meetingInstance?.agenda || null,
    participants: meetingInstance?.participantsInMeetingOnTH || [],
    platform: 'ZOOM',
    isActive: meetingData.inAMeeting,
    startTime: meetingInstance?.startTime || null,
    endTime: meetingInstance?.endTime || null,
    // Keep platform-specific data for gradual migration
    platformData: {
      meetingObject: meetingObject,
      meetingInstance: meetingInstance,
      participantsInMeetingOnZoom: meetingInstance?.participantsInMeetingOnZoom || [],
      participantsOpenedTH: meetingInstance?.participantsOpenedTH || [],
      platformMeetingInstanceId: meetingInstance?.platformMeetingInstanceId,
      platformMeetingId: meetingObject.platformMeetingId
    }
  };
};

// Individual selectors for specific data points
export const selectMeetingInstanceId = (state) => {
  const normalized = selectNormalizedMeetingState(state);
  return normalized.meetingInstanceId;
};

export const selectMeetingHostId = (state) => {
  const normalized = selectNormalizedMeetingState(state);
  return normalized.hostId;
};

export const selectCurrentAgenda = (state) => {
  const normalized = selectNormalizedMeetingState(state);
  return normalized.agenda;
};

export const selectMeetingParticipants = (state) => {
  const normalized = selectNormalizedMeetingState(state);
  return normalized.participants;
};

export const selectMeetingPlatform = (state) => {
  const normalized = selectNormalizedMeetingState(state);
  return normalized.platform;
};

export const selectIsMeetingActive = (state) => {
  const normalized = selectNormalizedMeetingState(state);
  return normalized.isActive;
};

// Platform-specific selectors for backward compatibility
export const selectZoomMeetingData = (state) => {
  const normalized = selectNormalizedMeetingState(state);
  if (normalized.platform !== 'ZOOM') return null;
  return normalized.platformData;
};

export const selectWebMeetingData = (state) => {
  const normalized = selectNormalizedMeetingState(state);
  if (normalized.platform !== 'WEB') return null;
  return normalized.platformData;
};

// Host and co-host detection
export const selectMeetingHosts = (state) => {
  const normalized = selectNormalizedMeetingState(state);
  
  if (!normalized.isActive || !normalized.platform) {
    return { host: null, coHosts: [] };
  }
  
  if (normalized.platform === 'WEB') {
    // Web meetings: host is stored in state.meeting.data.meetingObject.meetingHost as a user object
    const webHost = state.meeting.data?.meetingObject?.meetingHost;
    
    if (!webHost) {
      return { host: null, coHosts: [] };
    }
    
    // Convert web host format to consistent structure
    return {
      host: {
        userId: webHost._id,
        firstName: webHost.firstName,
        lastName: webHost.lastName,
        email: webHost.email,
        color: webHost.color,
        avatar: webHost.avatar,
        role: 'HOST',
        platform: 'WEB'
      },
      coHosts: [] // Web meetings don't have co-hosts
    };
  }
  
  // Zoom meetings: use existing structure from state.meeting.meetingHost
  return state.meeting.meetingHost || { host: null, coHosts: [] };
};

// Platform-agnostic host selector - returns the host user information
export const selectMeetingHost = (state) => {
  const hosts = selectMeetingHosts(state);
  return hosts.host;
};

// EPulse and Analytics selectors (unchanged for now)
export const selectEPulseData = (state) => {
  return state.meeting.ePulse?.data || [];
};

export const selectAnalyticsData = (state) => {
  return state.meeting.analytics || { live: false, data: [] };
};

// Validation helper for development
export const validateNormalizedMeetingState = (state) => {
  if (process.env.NODE_ENV !== 'development') return;
  
  const normalized = selectNormalizedMeetingState(state);
  
  if (normalized.isActive) {
    console.assert(normalized.meetingInstanceId, 'Active meeting missing meetingInstanceId');
    console.assert(normalized.platform, 'Active meeting missing platform');
    console.assert(normalized.hostId, 'Active meeting missing hostId');
  }
  
  if (normalized.agenda) {
    console.assert(normalized.agenda._id, 'Agenda missing _id');
    console.assert(normalized.agenda.live !== undefined, 'Agenda missing live status');
  }
  
  // Log normalized state in development for debugging
  if (normalized.isActive) {
    // console.log('🔍 Normalized Meeting State:', {
    //   platform: normalized.platform,
    //   meetingInstanceId: normalized.meetingInstanceId,
    //   hostId: normalized.hostId,
    //   hasAgenda: !!normalized.agenda,
    //   participantCount: normalized.participants?.length || 0
    // });
  }
};